/**
 * Canonical cart mutation coordinator (SEC-852 / U-02, KTD-1..4).
 *
 * Every first-party, theme-owned cart write is expected to run through this
 * single FIFO coordinator so that:
 *   - mutations are serialized (one logical transaction occupies one queue slot),
 *   - every response is validated before it is treated as cart state,
 *   - the mutation UI lock is reference-counted and restores each control's
 *     prior disabled/aria-disabled state,
 *   - the cart is reconciled from authoritative server state before the lock is
 *     released, and
 *   - failure / offline / integrity-uncertain states have defined, fail-closed
 *     behaviour that keeps checkout disabled until authoritative state renders.
 *
 * Vendor / app writes (Appstle, discount app proxy) are integration boundaries:
 * their controls are included in the UI lock, but their code is not patched.
 *
 * Consumers use it like:
 *   window.cartCoordinator.enqueue(async (ctx) => {
 *     await ctx.request(routes.cart_change_url, { line, quantity });
 *   }, { label: 'quantity' });
 *
 * A multi-request logical transaction issues several ctx.request(...) calls
 * inside ONE enqueue() callback; never call enqueue() from inside a txn
 * (that would self-deadlock the FIFO queue).
 */
(function () {
  if (window.cartCoordinator) return;

  var STRINGS = window.cartCoordinatorStrings || {};
  var DEFAULT_STRINGS = {
    generic: 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
    integrity: 'Der Warenkorb konnte nicht sicher aktualisiert werden. Bitte lade die Seite neu.',
    offline: 'Keine Verbindung. Der Warenkorb wird aktualisiert, sobald du wieder online bist.',
  };
  function str(key) {
    return STRINGS[key] || DEFAULT_STRINGS[key];
  }

  class CartMutationError extends Error {
    constructor(kind, status, payload) {
      var message = (payload && (payload.description || payload.message)) || str('generic');
      super(message);
      this.name = 'CartMutationError';
      this.kind = kind; // 'http' | 'parse' | 'network' | 'integrity'
      this.status = status || 0;
      this.payload = payload || null;
    }
  }

  class CartMutationCoordinator {
    constructor() {
      this._tail = Promise.resolve(); // FIFO chain tail
      this._active = 0; // in-flight transactions (invariant: <= 1)
      this._lockDepth = 0; // reference-counted UI lock
      this._priorState = new Map(); // control -> { disabled, aria }
      this._controlSelectors = new Set(['#CartDrawer-Checkout']);
      this._reconciler = null; // registered by the drawer (authoritative refresh)
      this._integrityUncertain = false;
      this.REQUEST_TIMEOUT_MS = 15000; // abort a stalled cart request so it cannot wedge the queue

      window.addEventListener('online', this._onOnline.bind(this));
    }

    /** Register the authoritative reconcile function (drawer refresh). */
    registerReconciler(fn) {
      if (typeof fn === 'function') this._reconciler = fn;
    }

    /** Consumers contribute the selectors of the controls they own. */
    registerControlSelectors(selectors) {
      (Array.isArray(selectors) ? selectors : [selectors]).forEach(s => {
        if (s) this._controlSelectors.add(s);
      });
    }

    get integrityUncertain() {
      return this._integrityUncertain;
    }

    get locked() {
      return this._lockDepth > 0;
    }

    /**
     * Enqueue a logical cart transaction.
     * @param {(ctx: { request: Function }) => Promise<any>} txn
     * @param {{ label?: string, lock?: boolean, reconcileOn?: 'always'|'error'|'never' }} [options]
     * @returns {Promise<any>} resolves with the txn result, rejects on failure.
     *
     * reconcileOn:
     *   'always' (default) — authoritative reconcile after every transaction.
     *   'error'            — only reconcile when the transaction failed
     *                        (use when the consumer already renders success inline).
     *   'never'            — never reconcile (fire-and-forget writes, e.g. note).
     */
    enqueue(txn, options) {
      var opts = Object.assign({ label: 'cart', lock: true, reconcileOn: 'always' }, options || {});
      var run = () => this._runTransaction(txn, opts);
      // Chain onto the tail regardless of the previous outcome so a rejected
      // transaction never wedges the queue.
      var result = this._tail.then(run, run);
      this._tail = result.then(
        () => {},
        () => {}
      );
      return result;
    }

    async _runTransaction(txn, opts) {
      if (this._integrityUncertain) {
        throw new CartMutationError('integrity', 0, { description: str('integrity') });
      }

      if (opts.lock) this._acquireLock();
      this._active++;

      var ctx = {
        request: (url, body, reqOpts) => this._request(url, body, reqOpts),
      };

      var error = null;
      var result;
      try {
        result = await txn(ctx);
      } catch (e) {
        error = e instanceof CartMutationError ? e : new CartMutationError('network', 0, { description: e && e.message });
      } finally {
        this._active--;
      }

      var shouldReconcile = opts.reconcileOn === 'always' || (opts.reconcileOn === 'error' && error);
      if (shouldReconcile) {
        try {
          await this._reconcile();
        } catch (reconErr) {
          // A failed reconcile means we cannot prove cart integrity: fail closed.
          // Enter the integrity state (it hard-disables the controls and owns the
          // message), then balance the lock counter via _releaseLock — which skips
          // the restore because the integrity flag is set. This keeps _lockDepth
          // from leaking, so the reference-counted lock still works after recover().
          this._enterIntegrityUncertain(error || reconErr);
          if (opts.lock) this._releaseLock();
          throw error || reconErr;
        }
      }

      if (opts.lock) this._releaseLock();

      if (error) {
        this._surfaceError(error);
        throw error;
      }

      window.dispatchEvent(new CustomEvent('cart:reconciled', { detail: { label: opts.label } }));
      return result;
    }

    /**
     * Private request primitive. Validates the HTTP status and Shopify error
     * payloads before returning parsed JSON. Never call directly — use the ctx
     * passed to an enqueue() callback so calls stay inside the queue slot.
     */
    async _request(url, body, reqOpts) {
      var config = typeof fetchConfig === 'function' ? fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      var init = Object.assign({}, config, reqOpts || {});
      init.method = init.method || 'POST';
      if (body !== undefined && body !== null) {
        init.body = typeof body === 'string' ? body : JSON.stringify(body);
      }

      // Guard every request with a timeout so a stalled (never-settling) socket
      // cannot wedge the shared FIFO queue for all later cart writes.
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = null;
      if (controller) {
        init.signal = controller.signal;
        timer = setTimeout(function () {
          controller.abort();
        }, this.REQUEST_TIMEOUT_MS);
      }

      var response;
      try {
        response = await fetch(url, init);
      } catch (e) {
        throw new CartMutationError('network', 0, { description: e && e.message });
      } finally {
        if (timer) clearTimeout(timer);
      }

      var text = await response.text();
      var json;
      try {
        json = text ? JSON.parse(text) : {};
      } catch (e) {
        throw new CartMutationError('parse', response.status, { description: str('generic') });
      }

      // Shopify returns a non-2xx status for cart errors; some endpoints also
      // report a numeric `status` field in an otherwise-200 error body.
      if (!response.ok || (json && typeof json.status === 'number')) {
        throw new CartMutationError('http', response.status, json);
      }

      return json;
    }

    async _reconcile() {
      if (typeof this._reconciler === 'function') {
        await this._reconciler();
      }
    }

    // --- reference-counted UI lock (KTD-2) ---------------------------------

    _controls() {
      var selector = Array.from(this._controlSelectors).join(',');
      if (!selector) return [];
      return document.querySelectorAll(selector);
    }

    _acquireLock() {
      this._lockDepth++;
      if (this._lockDepth > 1) return; // already locked by an outer slot
      this._priorState.clear();
      this._controls().forEach(el => {
        this._priorState.set(el, {
          disabled: 'disabled' in el ? el.disabled : undefined,
          aria: el.getAttribute('aria-disabled'),
        });
        if ('disabled' in el) el.disabled = true;
        el.setAttribute('aria-disabled', 'true');
      });
    }

    _releaseLock() {
      if (this._lockDepth === 0) return;
      this._lockDepth--;
      if (this._lockDepth > 0) return; // still held by an outer slot
      // While integrity is uncertain the controls stay hard-disabled until
      // recover() runs; do not restore them here.
      if (this._integrityUncertain) return;
      this._restoreControls();
    }

    _restoreControls() {
      this._priorState.forEach((prior, el) => {
        if (prior.disabled !== undefined && 'disabled' in el) el.disabled = prior.disabled;
        if (prior.aria === null) el.removeAttribute('aria-disabled');
        else el.setAttribute('aria-disabled', prior.aria);
      });
      this._priorState.clear();
    }

    // --- error / recovery surfaces -----------------------------------------

    _errorRegion() {
      return document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    }

    _surfaceError(error) {
      var region = this._errorRegion();
      if (region) region.textContent = (error && error.message) || str('generic');
      // eslint-disable-next-line no-console -- diagnostic, mirrors existing cart code
      console.error('[cart-coordinator]', error);
    }

    /**
     * Integrity could not be proven: keep the cart controls and checkout
     * disabled, surface a persistent message, and only recover once an
     * authoritative reconcile succeeds (manual retry or the `online` event).
     */
    _enterIntegrityUncertain(error) {
      this._integrityUncertain = true;
      // Fail closed: hard-disable every mutation control + checkout until an
      // authoritative reconcile succeeds (manual retry or the `online` event).
      this._controls().forEach(el => {
        if ('disabled' in el) el.disabled = true;
        el.setAttribute('aria-disabled', 'true');
      });
      var region = this._errorRegion();
      if (region) {
        region.textContent = str('integrity');
        region.setAttribute('role', 'alert');
      }
      // eslint-disable-next-line no-console -- diagnostic, mirrors existing cart code
      console.error('[cart-coordinator] integrity uncertain', error);
      window.dispatchEvent(new CustomEvent('cart:integrity-uncertain'));
    }

    /** Attempt to recover from an integrity-uncertain / offline state. */
    async recover() {
      try {
        await this._reconcile();
        this._integrityUncertain = false;
        // The integrity state abandoned the ref-count snapshot; reset the lock to
        // a clean released state and re-enable the freshly reconciled controls.
        this._lockDepth = 0;
        this._priorState.clear();
        this._enableControls();
        var region = this._errorRegion();
        if (region) {
          region.textContent = '';
          region.removeAttribute('role');
        }
        window.dispatchEvent(new CustomEvent('cart:reconciled', { detail: { label: 'recover' } }));
        return true;
      } catch (e) {
        this._surfaceError(e);
        return false;
      }
    }

    _enableControls() {
      this._controls().forEach(el => {
        if ('disabled' in el) el.disabled = false;
        el.removeAttribute('aria-disabled');
      });
    }

    _onOnline() {
      if (this._integrityUncertain) this.recover();
    }
  }

  window.CartMutationError = CartMutationError;
  window.cartCoordinator = new CartMutationCoordinator();
})();
